/**
 * synthetic-event.ts - the compiled path's React synthetic-event surface
 * (BUG-RUNTIME-154).
 *
 * React hands a handler a `SyntheticBaseEvent`, not the browser's event. Four
 * members exist ONLY on that wrapper and are therefore `undefined` for any
 * handler the compiler lowered:
 *
 *   nativeEvent            the original browser event
 *   persist()              a no-op since React 17 (pooling is gone)
 *   isDefaultPrevented()   defaultPrevented, as a method
 *   isPropagationStopped() whether a handler in this dispatch stopped it
 *
 * Everything else a handler reads (`type`, `target`, `key`, `preventDefault`,
 * `stopPropagation`, the modifier flags) is already on the native event and
 * already correct on this path - measured, three columns, in
 * `tasks/progress/2026-08-07-synthetic-events-spike.md`.
 *
 * So this is deliberately NOT a wrapper. The compat renderer wraps in a Proxy
 * (`createSyntheticEvent`, src/runtime/event-handlers.ts) because it must also
 * reproduce React's own-enumerable field copying and key/which normalization
 * for the libraries that suite runs. Reproducing that here would cost ~1.3 KB
 * gz on the smallest compiled app (measured: 1318 B gz for the Proxy module vs
 * 197 B gz for this one) and buy nothing the measurements found a caller for.
 * We ADD the four missing members to the dispatched event, once, in place.
 *
 * What that deliberately does not fix, and why it is safe to leave:
 *   - `e.nativeEvent === e` here, where React's are two objects. No measured
 *     caller compares them; every caller reads THROUGH nativeEvent.
 *   - `e.constructor.name` stays `MouseEvent` (React: `SyntheticBaseEvent`).
 *     The compat path diverges here too - it is a name, not a behaviour.
 *   - The added members stay on the event after dispatch, visible to other
 *     native listeners. React 17+ also keeps its wrapper alive after dispatch
 *     (pooling is gone), and a handler that stashes the event and reads it
 *     later MUST still see them - so restoring in a `finally` would be the
 *     bug, not the fix.
 *
 * Gating: every CALL SITE reads `__JETSHAKE_SYNTHETIC_EVENTS__` (the
 * `__SPREAD_ONCHANGE__` convention) and folds to the bare event when the build
 * proves no source in the graph names any of the four members - at which point
 * nothing imports this module and it shakes out, so a counter app pays 0 B.
 * The gate lives at the call sites, not in here, precisely so the import edge
 * disappears. Fail-closed: an absent or unproven define means armed.
 */
function _persist() { }
function _isDefaultPrevented() {
    return !!this.defaultPrevented;
}
function _isPropagationStopped() {
    return !!this.cancelBubble;
}
/**
 * Give a dispatched native event React's synthetic-only surface, in place.
 * Idempotent, and a no-op on an event that already carries it (a compat-made
 * synthetic event reaching a compiled handler in a mixed tree).
 */
export function syntheticize(event) {
    const e = event;
    if (e == null || e.nativeEvent !== undefined)
        return event;
    try {
        e.nativeEvent = e;
        e.persist = _persist;
        e.isDefaultPrevented = _isDefaultPrevented;
        e.isPropagationStopped = _isPropagationStopped;
    }
    catch {
        /* frozen or exotic event object: the handler gets it unaugmented */
    }
    return event;
}
